/**
 * feat-078 — Rehearsal Test Harness route page.
 *
 * Standalone, StaffRoute-gated page rendered at
 * `/quiz-management/rehearsal-test/:rehearsalId`. The rehearsal editor opens it
 * in a SEPARATE browser window via `window.open` so authors can edit curriculum
 * and run the harness side-by-side.
 *
 * That path is NOT a missed rename: the four browser routes are EXCLUDED BY
 * NAME from feat-198 (ADR 0001 §D-M) because a bookmarked URL is user state.
 * API paths moved; browser routes did not. The opener builds this URL by
 * TEMPLATE LITERAL at QuizContentManagement.tsx:119, so it is invisible to a
 * `<Link to=` or `path:` sweep — if you are renaming routes, that line and the
 * assertion in RehearsalTestHarness.button.test.tsx:93 are what you must find.
 *
 * The `rehearsalId` comes from the path. The header meta (`name`, pinned prompt
 * version `v`) is cosmetic and passed via query params by the opener — no extra
 * fetch / backend endpoint needed; grading + the statement suite are loaded by
 * the harness itself from the tenant-scoped admin endpoints keyed on the id.
 */
import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { RehearsalTestHarness } from './RehearsalTestHarness';

const RehearsalTestHarnessPage: React.FC = () => {
  const { rehearsalId } = useParams<{ rehearsalId: string }>();
  const [searchParams] = useSearchParams();

  const name = searchParams.get('name') ?? '';
  const vRaw = searchParams.get('v');
  const pinnedVersionNumber =
    vRaw != null && vRaw !== '' && !Number.isNaN(Number(vRaw))
      ? Number(vRaw)
      : null;

  useEffect(() => {
    const prev = document.title;
    document.title = name ? `Test harness · ${name}` : 'Rehearsal test harness';
    return () => {
      document.title = prev;
    };
  }, [name]);

  if (!rehearsalId) {
    return (
      <div className="h-screen flex items-center justify-center text-sm text-gray-500">
        Missing rehearsal id.
      </div>
    );
  }

  return (
    <RehearsalTestHarness
      rehearsalId={rehearsalId}
      rehearsalName={name}
      pinnedVersionNumber={pinnedVersionNumber}
      onClose={() => window.close()}
    />
  );
};

export default RehearsalTestHarnessPage;
